import type { IRatingMatrixFrameworkInstrumentStep } from '__generated__';
import { baseApi } from 'store/api';
import { API, asPatchBody, normalizeError, type PatchBody } from 'utils/api-client';
import { getSortOrderPatches, type SortOrderPatch } from './helpers';

export const ratingMatrixFrameworkInstrumentStepApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createInstrumentStep: builder.mutation<
      IRatingMatrixFrameworkInstrumentStep,
      {
        frameworkTemplateID: number;
        instrumentTrackID: number;
        title: string;
        description?: string | null;
        sortOrder: number;
      }
    >({
      async queryFn({ instrumentTrackID, title, description, sortOrder }) {
        try {
          const { data } = await API.api.ratingMatrixFrameworkInstrumentStepCreateCreate({
            instrumentTrackID,
            title,
            description: description ?? null,
            sortOrder,
          });
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
    }),

    patchInstrumentStep: builder.mutation<
      string,
      {
        id: number;
        frameworkTemplateID: number;
        patch: PatchBody<
          Pick<IRatingMatrixFrameworkInstrumentStep, 'title' | 'description' | 'sortOrder'>
        >;
      }
    >({
      async queryFn({ id, patch }) {
        try {
          const { data } = await API.api.ratingMatrixFrameworkInstrumentStepPatchPartialUpdate(
            { id },
            asPatchBody(patch),
          );
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
    }),

    deleteInstrumentStep: builder.mutation<
      IRatingMatrixFrameworkInstrumentStep,
      { id: number; frameworkTemplateID: number }
    >({
      async queryFn({ id }) {
        try {
          const { data } = await API.api.ratingMatrixFrameworkInstrumentStepDeleteDelete({ id });
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
    }),

    reorderInstrumentSteps: builder.mutation<
      void,
      {
        frameworkTemplateID: number;
        items: Array<{ id: number; sortOrder: number }>;
        startIndex: number;
        endIndex: number;
      }
    >({
      async queryFn({ items, startIndex, endIndex }) {
        try {
          const patches = getSortOrderPatches(items, startIndex, endIndex);
          await Promise.all(
            patches.map((patch: SortOrderPatch) =>
              API.api.ratingMatrixFrameworkInstrumentStepPatchPartialUpdate(
                { id: patch.id },
                asPatchBody({ sortOrder: patch.sortOrder }),
              ),
            ),
          );
          return { data: undefined };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
    }),
  }),
});

export const {
  useCreateInstrumentStepMutation,
  usePatchInstrumentStepMutation,
  useDeleteInstrumentStepMutation,
  useReorderInstrumentStepsMutation,
} = ratingMatrixFrameworkInstrumentStepApi;
